import { useMemo } from 'react';
import type { WidenMediaElement } from '@hub/libs/widen-utils';
import { MediaElementType } from '@hub/libs/widen-utils';
import type { IbhMaterialLightResponseDto, IbhWidenDto } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhWidenDtoTypeEnum } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import type { ShapeDescriptor } from '@hub/bearing-hub/common/shapes/shapes';

const MARKETING_BADGE_LIST = {
  pfasFree: 'pjcxxtsqjs',
  BPABPSFree: 'hvbiplntoq',
};

const mapArticleWidenTypeToMediaElementType = (mediaType: IbhWidenDtoTypeEnum): MediaElementType | undefined => {
  if (mediaType === 'image') {
    return MediaElementType.IMAGE;
  }
  if (mediaType === 'drawing') {
    return MediaElementType.DRAWING;
  }
  return undefined;
};

type UseProductMediaParams = {
  widens: IbhWidenDto[];
  title: string;
  widenShortKey: string;
  shape: ShapeDescriptor;
  material?: IbhMaterialLightResponseDto;
};

const getMarketingBadges = (material?: IbhMaterialLightResponseDto): string[] => {
  const marketingBadges: string[] = [];

  if (material?.PFASFree) {
    marketingBadges.push(MARKETING_BADGE_LIST.pfasFree);
  }

  if (material?.BPABPSFree) {
    marketingBadges.push(MARKETING_BADGE_LIST.BPABPSFree);
  }

  return marketingBadges;
};

export const useProductMedia = ({
  widens,
  title,
  widenShortKey,
  material,
  shape,
}: UseProductMediaParams): WidenMediaElement[] => {
  return useMemo(() => {
    const marketingBadges = getMarketingBadges(material);
    const mediaList = widens.filter(
      widen => widen.type === IbhWidenDtoTypeEnum.Image || widen.type === IbhWidenDtoTypeEnum.Drawing
    );
    const getSortedAndMappedMedia = (
      mediaListToSort: IbhWidenDto[],
      shortKey: string,
      description: string,
      badges: string[]
    ): WidenMediaElement[] => {
      return mediaListToSort.reduce((acc: WidenMediaElement[], data) => {
        const mappedType = mapArticleWidenTypeToMediaElementType(data.type);
        const mappedItem = {
          type: mappedType,
          filename: data.externalId,
          shortCode: data.externalId,
          marketingBadges: badges,
          description: mappedType === MediaElementType.DRAWING ? `${description} drawing` : description,
        };

        if (data.externalId === shortKey) {
          acc.unshift(mappedItem);
        } else {
          acc.push(mappedItem);
        }

        return acc;
      }, []);
    };
    switch (mediaList.length) {
      case 0:
        return [
          {
            type: MediaElementType.IMAGE,
            filename: widenShortKey,
            shortCode: widenShortKey,
            description: title,
            marketingBadges,
          },
        ];
      case 1:
        return [
          {
            type: MediaElementType.IMAGE,
            filename: widenShortKey,
            shortCode: widenShortKey,
            description: title,
            marketingBadges,
          },
          {
            type: MediaElementType.DRAWING,
            filename: shape.widenShortKeySchema,
            shortCode: shape.widenShortKeySchema,
            description: `${title} drawing`,
          },
        ];
      default: {
        return getSortedAndMappedMedia(mediaList, widenShortKey, title, marketingBadges);
      }
    }
  }, [widens, title, widenShortKey, material, shape]);
};
